import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'primeicons/primeicons.css';
import { pdf } from '@react-pdf/renderer';
import { carteraApi } from './api';
import { CapturarTelefonoModal } from './CapturarTelefonoModal';
import { CotizacionPdfAction } from './CotizacionPdfAction';
import { CambioCotizacionAviso } from './CambioCotizacionAviso';
import { BotVentasSwitch } from './BotVentasSwitch';
import { BotAjustesEngrane } from './botAjustes/BotAjustesEngrane';
import { ModoHiloToggle } from './ModoHiloToggle';
import { AvisosColaModal } from './AvisosColaModal';
import { EstadoCuentaPDF } from './EstadoCuentaPDF';
import { RegistrarPagoModal } from './RegistrarPagoModal';
import ModalPortal from './ModalPortal';
import { buildEdoCuentaView, buildEdoCuentaPdfData, fmtDate } from './edoCuenta';
import {
  money, iniciales, PROD, prodCorto, stageStyle, etapaLabelDe, Pill,
} from './helpers';

const POLL_MS = 20000;
// 🔴 Apagado a mano durante el desarrollo (7-ago-2026). Poner en true para
// devolver el refresco automático cada POLL_MS. Ver el useEffect del polling.
const POLL_ACTIVO = true;

/*
 * Alertas que dejan los botones de las plantillas (backend: gestiones con
 * origen=ia). El usuario las quiere VISIBLES, no escondidas: badge en la lista
 * y, para "dice que pagó", una tarjeta arriba del panel derecho que no se
 * pueda ignorar. Ámbar = el del módulo (#FFF6E5 / #F4E2BC / #8a6d2a).
 */
const ALERTA = {
  ya_pague: {
    label: 'Dice que pagó', corto: '💰 Dice que pagó', icon: 'pi-money-bill',
    bg: '#FFF6E5', borde: '#F4E2BC', color: '#8a6d2a',
  },
  asesor: {
    label: 'Pidió asesor', corto: '🙋 Pidió asesor', icon: 'pi-user',
    bg: '#EAF7FC', borde: '#CBE7F4', color: '#1f6f8c',
  },
  estado_cuenta: {
    label: 'Pidió su estado de cuenta', corto: '📄 Pidió su estado', icon: 'pi-file',
    bg: '#EEF1F4', borde: '#E2E8F0', color: '#475569',
  },
};
const alertaMeta = (motivo) => ALERTA[motivo] || ALERTA.estado_cuenta;

/*
 * Traduce el resultado del aviso al encargado (gestion.meta.avisos) a algo
 * legible. El backend guarda 'enviado' | 'omitido:<causa>' | 'fallido:<código>'
 * | 'error'; aquí se dice en español y con el destino, porque el punto es que
 * el cobrador vea A QUIÉN se le avisó, no solo que se intentó.
 */
const avisosTexto = (avisos) => {
  if (!avisos) return [];
  const lineas = [];
  const describe = (estado, destino) => {
    if (!estado) return null;
    if (estado === 'enviado') return `Avisado a ${destino || 'destino no configurado'}`;
    if (estado.startsWith('omitido')) return 'No se avisó (desactivado en configuración)';
    if (estado.startsWith('fallido')) return `No se pudo avisar — Meta rechazó (${estado.split(':')[1] || 'error'})`;
    return 'No se pudo avisar (error de envío)';
  };
  const wa = describe(avisos.whatsapp, avisos.whatsapp_destino);
  const mail = describe(avisos.email, avisos.email_destino);
  if (wa) lineas.push({ k: 'wa', icon: 'pi-whatsapp', txt: `WhatsApp: ${wa}` });
  if (mail) lineas.push({ k: 'mail', icon: 'pi-envelope', txt: `Correo: ${mail}` });
  return lineas;
};

/*
 * 🔴 "2026-09-01" a secas lo parsea JS como medianoche UTC, y al pintarlo en
 * hora de México (UTC-6) retrocede al día anterior: el vencimiento del 1-sep
 * se veía como "31 de agosto". Las fechas SIN hora son fechas de calendario,
 * no instantes, así que se anclan a medianoche LOCAL.
 */
/*
 * Los errores de Meta llegan en inglés y con jerga de API ("Authentication
 * Error", "(#132001) Template name does not exist"). Al cobrador eso no le dice
 * si el problema es del cliente, del mensaje o del sistema — y lo primero que
 * piensa es que el número está mal.
 *
 * Se traduce a qué HACER. El texto crudo se conserva en el `title` del icono
 * para quien tenga que dar soporte.
 */
const ERRORES_ENVIO = [
  [/authentication|oauth|expired|190/i, 'Falta renovar la conexión con WhatsApp. Avisa a soporte.'],
  [/132001|does not exist/i, 'La plantilla no está cargada en WhatsApp con ese nombre o idioma. Avisa a soporte.'],
  [/132000|number of parameters/i, 'La plantilla y los datos no coinciden. Avisa a soporte.'],
  [/131047|re-?engagement/i, 'Pasaron más de 24 h desde su último mensaje: solo se le puede escribir con plantilla.'],
  [/131026|not a whatsapp user|undeliverable/i, 'Ese número no tiene WhatsApp o no acepta mensajes.'],
  [/80007|130429|rate limit|too many/i, 'WhatsApp está limitando los envíos. Espera unos minutos y reintenta.'],
  [/131031|account.*(restrict|block)/i, 'La cuenta de WhatsApp está restringida. Avisa a soporte.'],
  [/timeout|timed out|could not resolve|connection/i, 'No se pudo contactar a WhatsApp. Reintenta en un momento.'],
];

const errorEnvioLegible = (crudo) => {
  const texto = String(crudo || '');
  if (!texto) return 'WhatsApp rechazó el mensaje.';
  const encontrado = ERRORES_ENVIO.find(([patron]) => patron.test(texto));
  return encontrado ? encontrado[1] : texto;
};

const fechaValida = (valor) => {
  if (!valor) return null;
  const texto = typeof valor === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(valor.trim())
    ? `${valor.trim()}T00:00:00`
    : valor;
  const fecha = new Date(texto);
  return Number.isNaN(fecha.getTime()) ? null : fecha;
};

const horaDe = (valor) => {
  const fecha = fechaValida(valor);
  return fecha ? fecha.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '';
};

const tiempoLista = (valor) => {
  const fecha = fechaValida(valor);
  if (!fecha) return '';
  const hoy = new Date();
  if (fecha.toDateString() === hoy.toDateString()) return horaDe(valor);
  const dias = Math.floor((hoy.getTime() - fecha.getTime()) / 86400000);
  if (dias < 7) return `${Math.max(1, dias)}d`;
  return fecha.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
};

const diaClave = (valor) => {
  const fecha = fechaValida(valor);
  return fecha ? fecha.toISOString().slice(0, 10) : 'sin-fecha';
};

const diaLabel = (valor) => {
  const fecha = fechaValida(valor);
  if (!fecha) return 'Sin fecha';
  const hoy = new Date();
  const ayer = new Date();
  ayer.setDate(ayer.getDate() - 1);
  if (fecha.toDateString() === hoy.toDateString()) return 'Hoy';
  if (fecha.toDateString() === ayer.toDateString()) return 'Ayer';
  return fecha.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
};

const fechaPlantilla = (valor) => {
  const fecha = fechaValida(valor);
  return fecha ? fecha.toLocaleDateString('es-MX', { day: 'numeric', month: 'long' }) : null;
};

// "31 jul, 16:20" — cuándo tocó el botón el cliente.
const fechaHoraCorta = (valor) => {
  const fecha = fechaValida(valor);
  if (!fecha) return '—';
  return `${fecha.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}, ${horaDe(valor)}`;
};

const vistaPreviaTemplate = (template, valores = {}) => {
  if (template === 'aviso_pago_vencido') {
    return `Hola ${valores.nombre || '—'}, tu pago venció y sigue pendiente.\n\nDías de atraso: ${valores.dias || '0'}\nMensualidades pendientes: ${valores.mensualidades || '0'}\nTotal para ponerte al corriente: ${valores.total || '$0.00'} (ya incluye moratorios)\n\nSi ya pagaste, tócalo abajo. — PEICAR`;
  }
  if (template === 'pago_vence_hoy') {
    return `Hola ${valores.nombre || '—'}, hoy vence tu ${valores.concepto || 'pago'} de $${valores.monto || '0.00'} de ${valores.auto || 'tu unidad'}. Si ya pagaste, toca “Ya pagué”. — PEICAR`;
  }
  return `Hola ${valores.nombre || '—'}, te recordamos que tu ${valores.concepto || 'pago'} de $${valores.monto || '0.00'} de ${valores.auto || 'tu unidad'} vence el ${valores.fecha || '—'}. — PEICAR`;
};

const tamanoArchivo = (bytes) => {
  const total = Number(bytes);
  if (!Number.isFinite(total) || total <= 0) return null;
  if (total < 1024) return `${total} B`;
  if (total < 1024 * 1024) return `${(total / 1024).toFixed(1)} KB`;
  return `${(total / (1024 * 1024)).toFixed(1)} MB`;
};

const errorApi = (error, fallback) => {
  const errors = error?.response?.data?.errors;
  const primero = errors ? Object.values(errors).flat()[0] : null;
  return primero || error?.response?.data?.message || fallback;
};

const estadoBloqueo = {
  opt_out: 'El cliente pidió BAJA — no se le pueden enviar plantillas.',
  numero_equivocado: 'El número fue marcado como equivocado.',
  bloqueada: 'La conversación está bloqueada.',
};

const ListaSkeleton = () => (
  <div style={{ padding: '8px 15px 18px' }}>
    {[0, 1, 2, 3].map((i) => (
      <div key={i} style={{ display: 'flex', gap: 11, alignItems: 'center', padding: '10px 0' }}>
        <div className="sk" style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div className="sk" style={{ width: '72%', height: 11, marginBottom: 8 }} />
          <div className="sk" style={{ width: '92%', height: 9 }} />
        </div>
      </div>
    ))}
  </div>
);

const MensajesSkeleton = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 18 }}>
    <div className="sk" style={{ width: '46%', height: 58, borderRadius: 14 }} />
    <div className="sk" style={{ width: '58%', height: 72, borderRadius: 14, alignSelf: 'flex-end' }} />
    <div className="sk" style={{ width: '36%', height: 52, borderRadius: 14 }} />
  </div>
);

const MediaMensaje = ({ conversacionId, mensaje }) => {
  const disponible = Boolean(mensaje.s3_path);
  const precarga = ['image', 'sticker', 'audio', 'video'].includes(mensaje.tipo);
  const [mediaUrl, setMediaUrl] = useState(null);
  const [cargando, setCargando] = useState(precarga && disponible);
  const [error, setError] = useState(null);
  const nombre = mensaje.media_filename || (mensaje.tipo === 'document' ? 'Documento' : 'Archivo');
  const tamano = tamanoArchivo(mensaje.media_size);

  useEffect(() => {
    if (!precarga || !disponible) return undefined;
    const ctrl = new AbortController();
    let objectUrl = null;

    carteraApi.getConversacionMedia(conversacionId, mensaje.id, ctrl.signal)
      .then((res) => {
        objectUrl = URL.createObjectURL(res.data);
        setMediaUrl(objectUrl);
      })
      .catch((e) => {
        if (e?.code !== 'ERR_CANCELED') setError('Archivo no disponible');
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setCargando(false);
      });

    return () => {
      ctrl.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [conversacionId, disponible, mensaje.id, precarga]);

  const descargarDocumento = async () => {
    setCargando(true);
    setError(null);
    try {
      const res = await carteraApi.getConversacionMedia(conversacionId, mensaje.id);
      const objectUrl = URL.createObjectURL(res.data);
      const enlace = document.createElement('a');
      enlace.href = objectUrl;
      enlace.download = nombre;
      enlace.click();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (e) {
      setError('Archivo no disponible');
    } finally {
      setCargando(false);
    }
  };

  if (!disponible || error) {
    return <div className="dc-media-unavailable"><span className="pi pi-ban" /> Archivo no disponible</div>;
  }

  if (mensaje.tipo === 'document') {
    return (
      <button type="button" className="dc-media-document" onClick={descargarDocumento} disabled={cargando}>
        <span className={`pi ${cargando ? 'pi-spin pi-spinner' : 'pi-file'}`} />
        <span><b>{nombre}</b>{tamano && <small>{tamano}</small>}</span>
        <span className="pi pi-download" />
      </button>
    );
  }

  if (cargando) {
    return <div className="dc-media-loading"><span className="pi pi-spin pi-spinner" /> Cargando archivo…</div>;
  }

  if (mensaje.tipo === 'audio') {
    return <audio className="dc-media-audio" controls preload="metadata" src={mediaUrl} />;
  }

  if (mensaje.tipo === 'video') {
    return <video className="dc-media-video" controls preload="metadata" src={mediaUrl} />;
  }

  return (
    <div>
      <a href={mediaUrl} target="_blank" rel="noreferrer" className="dc-media-image-link">
        <img
          className={mensaje.tipo === 'sticker' ? 'dc-media-sticker' : 'dc-media-image'}
          src={mediaUrl}
          alt={mensaje.body || 'Imagen recibida por WhatsApp'}
        />
      </a>
      {mensaje.body && <div className="dc-media-caption">{mensaje.body}</div>}
    </div>
  );
};

export const CarteraConversaciones = ({
  preselectId,
  onSelectConversacion,
  onOpenEdo,
}) => {
  const [query, setQuery] = useState('');
  /* Dos bandejas sobre la MISMA pantalla: 'cobranza' son los clientes con
   * contrato de siempre; 'cotizaciones' son los leads que llegaron por la
   * landing y todavía no son clientes (los marca meta.tipo en el backend).
   * Van aquí y no como pestaña de CarteraPage porque el chat, las plantillas y
   * el envío son idénticos — lo único que cambia es a quién estás viendo. */
  const [bandeja, setBandeja] = useState('cobranza');
  const [contadores, setContadores] = useState({ cobranza: 0, cotizaciones: 0 });
  const [conversaciones, setConversaciones] = useState([]);
  const [pendientes, setPendientes] = useState([]);
  const [templates, setTemplates] = useState([]);
  // Banderas de canal que manda el backend. Van en la respuesta y no en el
  // bundle para que apagar la env y refrescar surta efecto sin redeploy.
  const [features, setFeatures] = useState({});
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [selectedKey, setSelectedKey] = useState(preselectId || null);
  const [selectedCreditoId, setSelectedCreditoId] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [mensajesLoading, setMensajesLoading] = useState(false);
  const [mensajesError, setMensajesError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [olderLoading, setOlderLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [telefonoInput, setTelefonoInput] = useState('');
  const [templatePendiente, setTemplatePendiente] = useState(null);
  const [templateModal, setTemplateModal] = useState(null);
  const [pagoModal, setPagoModal] = useState(null);
  const [pagoModalLoading, setPagoModalLoading] = useState(false);
  const [refrescando, setRefrescando] = useState(false);
  const [detalleMensaje, setDetalleMensaje] = useState(null);
  const [avisosData, setAvisosData] = useState(null);
  const [colaOpen, setColaOpen] = useState(false);
  const [avisosBusy, setAvisosBusy] = useState('');
  const [avisosNotice, setAvisosNotice] = useState(null);
  const [nuevos, setNuevos] = useState(false);
  // Alerta que se está resolviendo (id de la gestión) y envío del PDF.
  const [resolviendo, setResolviendo] = useState(null);
  const [alertaAbiertaId, setAlertaAbiertaId] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [aviso, setAviso] = useState(null);
  const fileRef = useRef(null);
  const lista = useMemo(() => [...conversaciones, ...pendientes], [conversaciones, pendientes]);
  const selected = useMemo(
    () => lista.find((fila) => String(fila.key ?? fila.id) === String(selectedKey)) || null,
    [lista, selectedKey],
  );
  const creditos = selected?.creditos || [];
  const selectedCredito = creditos.find((credito) => String(credito.id) === String(selectedCreditoId))
    || creditos[0]
    || null;
  const selectedStableKey = selected?.key ?? selected?.id ?? null;
  const selectedId = selected?.id || null;
  const primerCreditoId = selected?.creditos?.[0]?.id || null;
  const primerCreditoTelefono = selected?.creditos?.[0]?.telefono || '';
  const chatBodyRef = useRef(null);
  const mensajesRef = useRef([]);

  /*
   * 🔴 LA SELECCIÓN SE ANCLA AL CONTRATO, NO A LA TARJETA (11-ago-2026).
   *
   * `selected` se resuelve buscando `key` en la lista, y esa key es EFÍMERA
   * por diseño: la bandeja agrupa por teléfono, así que capturar un número
   * FUNDE dos tarjetas en una y la key de la que estabas viendo desaparece.
   * La fusionada se queda con la del contrato de más días vencido y, con
   * empate, lo decide el orden de la query — o sea, un volado.
   *
   * Caso real que lo destapó: BULLIT (user 212) tenía AP039 y AP046, los dos
   * con 10 días vencidos. Al capturarle el teléfono desde AP046 (`nuevo-37`)
   * las dos tarjetas se fundieron, ganó AP039 (`nuevo-31`), y `selected` se
   * volvió null: el panel derecho se vació y había que buscar la tarjeta a
   * mano. Justo después de la acción que la movió, y sin ningún error.
   *
   * Pasa lo mismo al mandar la primera plantilla —el contrato salta de
   * `pendientes` (`nuevo-{id}`) a un hilo real (`{conversacion_id}`)— y ese
   * camino ya lo resolvía a mano con el conversacion_id de la respuesta. Esto
   * lo generaliza: el CONTRATO sobrevive a las tres mudanzas, así que se
   * guarda su id y, cuando la lista se recarga y la selección queda huérfana,
   * se vuelve a enganchar sola.
   *
   * Importante: el ancla NO se limpia cuando `selected` se va a null. Ese es
   * precisamente el momento en que hace falta.
   */
  const anclaCreditoRef = useRef(null);

  // La lista es la que scrollea, no el documento: `nearest` evita que la
  // página entera brinque al reenganchar.
  const enfocarFila = (key) => {
    document
      .querySelector(`[data-fila-key="${CSS.escape(String(key))}"]`)
      ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  useEffect(() => {
    if (selectedCredito?.id) anclaCreditoRef.current = String(selectedCredito.id);
  }, [selectedCredito?.id]);

  useEffect(() => {
    // Solo actúa si la selección quedó huérfana. Si la tarjeta sigue viva, o
    // si una búsqueda la filtró (find no la encuentra), no toca nada.
    if (selected || !anclaCreditoRef.current || lista.length === 0) return;
    const destino = lista.find((fila) => (fila.creditos || [])
      .some((credito) => String(credito.id) === String(anclaCreditoRef.current)));
    if (!destino) return;

    const key = String(destino.key ?? destino.id);
    setSelectedKey(key);
    setSelectedCreditoId(anclaCreditoRef.current);
    if (onSelectConversacion) onSelectConversacion(key);
    requestAnimationFrame(() => enfocarFila(key));
  }, [lista, selected, onSelectConversacion]);
  // Contrato cuyo teléfono se está capturando en el modal. null = cerrado.
  const [telefonoModal, setTelefonoModal] = useState(null);
  // Hilos donde ya se cerró el modal a mano: no se vuelve a abrir solo al
  // re-renderizar ni en cada vuelta del polling de 20s.
  const modalOmitidoRef = useRef(new Set());

  // Abrir el hilo de un contrato sin teléfono no sirve de nada: no se puede
  // mandar plantilla ni texto. Se pide el número de entrada, sobre el chat.
  useEffect(() => {
    if (!selected || !selectedCredito?.sin_telefono) return;
    const clave = String(selected.key ?? selected.id ?? '');
    if (modalOmitidoRef.current.has(clave)) return;
    setTelefonoModal(selectedCredito);
  }, [selected, selectedCredito]);

  const cerrarTelefonoModal = () => {
    if (selected) modalOmitidoRef.current.add(String(selected.key ?? selected.id ?? ''));
    setTelefonoModal(null);
  };

  const telefonoGuardado = async (telefono, creditoId) => {
    modalOmitidoRef.current.delete(String(selected?.key ?? selected?.id ?? ''));
    setTelefonoModal(null);
    setSendError(null);
    setTemplateModal((actual) => actual ? {
      ...actual,
      credito: { ...actual.credito, sin_telefono: false, telefono },
    } : actual);
    // Manda el contrato que se acaba de editar, no el que estuviera
    // seleccionado: el modal también se abre desde templateModal. Guardar un
    // teléfono es LA acción que funde tarjetas, así que el ancla se pone antes
    // de recargar (ver anclaCreditoRef).
    if (creditoId) anclaCreditoRef.current = String(creditoId);
    // Refresco silencioso: setLoading(true) desmontaría el panel y perdería el hilo.
    await cargarLista(() => { }, undefined, true);
  };

  useEffect(() => {
    setSelectedKey((actual) => (
      String(actual ?? '') === String(preselectId ?? '') ? actual : (preselectId || null)
    ));
  }, [preselectId]);

  const cargarLista = useCallback(async (setBusy, signal, silencioso = false) => {
    setBusy(true);
    if (!silencioso) setListError(null);
    try {
      const res = await carteraApi.getConversaciones({
        ...(query.trim() ? { search: query.trim() } : {}),
        tipo: bandeja,
      }, signal);
      if (signal?.aborted) return;
      setConversaciones(res?.data?.data || []);
      setPendientes(res?.data?.pendientes || []);
      setTemplates(res?.data?.templates || []);
      setFeatures(res?.data?.features || {});
      setContadores(res?.data?.contadores || { cobranza: 0, cotizaciones: 0 });
    } catch (error) {
      if (signal?.aborted || error?.code === 'ERR_CANCELED') return;
      if (!silencioso) setListError(errorApi(error, 'No se pudieron cargar las conversaciones.'));
    } finally {
      if (!signal?.aborted) setBusy(false);
    }
  }, [query, bandeja]);

  const cargarAvisos = useCallback(async (signal, tab = null) => {
    try {
      const res = await carteraApi.getAvisos(signal, tab);
      if (!signal?.aborted) setAvisosData(res?.data || null);
    } catch (error) {
      if (!signal?.aborted && error?.code !== 'ERR_CANCELED') {
        setAvisosNotice({ type: 'error', text: errorApi(error, 'No se pudo cargar la cola.') });
      }
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    const timer = setTimeout(
      () => cargarLista(setListLoading, ctrl.signal),
      query ? 300 : 0,
    );
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [cargarLista, query]);

  useEffect(() => {
    const ctrl = new AbortController();
    cargarAvisos(ctrl.signal);
    return () => ctrl.abort();
  }, [cargarAvisos]);

  const scrollAlFinal = useCallback((behavior = 'smooth') => {
    const panel = chatBodyRef.current;
    if (!panel) return;
    panel.scrollTo({ top: panel.scrollHeight, behavior });
    setNuevos(false);
  }, []);

  const mezclarMensajes = useCallback((entrantes, reemplazar = false) => {
    const actuales = reemplazar ? [] : mensajesRef.current;
    const porId = new Map(actuales.map((mensaje) => [mensaje.id, mensaje]));
    entrantes.forEach((mensaje) => porId.set(mensaje.id, mensaje));
    const mezcla = [...porId.values()].sort((a, b) => Number(a.id) - Number(b.id));
    mensajesRef.current = mezcla;
    setMensajes(mezcla);
    return mezcla;
  }, []);

  const cargarMensajes = useCallback(async (
    conversacionId,
    setBusy,
    signal,
    { silencioso = false, reemplazar = false } = {},
  ) => {
    setBusy(true);
    if (!silencioso) setMensajesError(null);
    const panel = chatBodyRef.current;
    const estabaAbajo = !panel || panel.scrollHeight - panel.scrollTop - panel.clientHeight < 72;
    const idsAntes = new Set(mensajesRef.current.map((mensaje) => mensaje.id));
    try {
      const res = await carteraApi.getConversacionMensajes(conversacionId, {}, signal);
      if (signal?.aborted) return;
      const entrantes = [...(res?.data?.data || [])].reverse();
      const hayNuevos = !reemplazar && entrantes.some((mensaje) => !idsAntes.has(mensaje.id));
      mezclarMensajes(entrantes, reemplazar);
      setHasMore(Boolean(res?.data?.pagination?.has_more));
      if (reemplazar || (hayNuevos && estabaAbajo)) {
        requestAnimationFrame(() => scrollAlFinal('auto'));
      } else if (hayNuevos) {
        setNuevos(true);
      }
    } catch (error) {
      if (signal?.aborted || error?.code === 'ERR_CANCELED') return;
      if (!silencioso) setMensajesError(errorApi(error, 'No se pudo cargar el hilo.'));
    } finally {
      if (!signal?.aborted) setBusy(false);
    }
  }, [mezclarMensajes, scrollAlFinal]);

  useEffect(() => {
    setDraft('');
    setSendError(null);
    setAviso(null);
    setTemplatePendiente(null);
    setNuevos(false);
    setMensajes([]);
    mensajesRef.current = [];
    setHasMore(false);
    setSelectedCreditoId(primerCreditoId);
    setTelefonoInput(primerCreditoTelefono);

    if (!selectedId) {
      setMensajesLoading(false);
      return undefined;
    }

    const ctrl = new AbortController();
    cargarMensajes(selectedId, setMensajesLoading, ctrl.signal, { reemplazar: true });
    return () => ctrl.abort();
  }, [cargarMensajes, primerCreditoId, primerCreditoTelefono, selectedId, selectedStableKey]);

  useEffect(() => {
    if (!selected?.id) return undefined;
    const ctrl = new AbortController();
    carteraApi.marcarConversacionLeida(selected.id, {}, ctrl.signal)
      .then(() => {
        setConversaciones((actuales) => actuales.map((fila) => (
          fila.id === selected.id ? { ...fila, no_leidos: 0 } : fila
        )));
      })
      .catch(() => { });
    return () => ctrl.abort();
  }, [selected?.id]);

  /*
   * Refresco automático de la bandeja y del hilo abierto cada POLL_MS.
   *
   * Estuvo apagado del 7 al 11-ago-2026 durante el desarrollo (llenaba el log
   * de peticiones). Se reactivó con POLL_ACTIVO; el botón "Actualizar" del
   * encabezado (refrescarTodo) convive con el timer, no hay que quitarlo.
   *
   * 🔴 Cada vuelta REEMPLAZA la lista, y con ella las keys de las tarjetas.
   * Que la selección sobreviva a eso no es gratis: lo sostiene anclaCreditoRef
   * (más arriba). Si algún día se toca esto, probar que al fundirse dos
   * tarjetas —o al nacer un hilo— el panel derecho no se quede vacío.
   */
  useEffect(() => {
    if (!POLL_ACTIVO) return undefined;

    const ctrl = new AbortController();
    const timer = setInterval(() => {
      cargarLista(() => { }, ctrl.signal, true);
      if (selected?.id) {
        cargarMensajes(selected.id, () => { }, ctrl.signal, { silencioso: true });
      }
    }, POLL_MS);
    return () => {
      clearInterval(timer);
      ctrl.abort();
    };
  }, [cargarLista, cargarMensajes, selected?.id]);

  const seleccionar = (fila) => {
    const key = String(fila.key ?? fila.id);
    setSelectedKey(key);
    if (onSelectConversacion) onSelectConversacion(key);
  };

  const seleccionarCredito = (id) => {
    const credito = creditos.find((item) => String(item.id) === String(id));
    setSelectedCreditoId(id);
    setTelefonoInput(credito?.telefono || '');
    setSendError(null);
    setTemplatePendiente(null);
  };

  const agregarMensajeRespuesta = (respuesta) => {
    const mensaje = respuesta?.data?.data;
    if (mensaje?.id) mezclarMensajes([mensaje]);
  };

  const prepararTemplate = async (template, credito = selectedCredito) => {
    if (!credito || sending) return;
    setTemplatePendiente(template);
    setTemplateModal({ template, credito, loading: true, error: null, params: [], valores: {} });
    setSendError(null);
    try {
      const res = await carteraApi.getPlantillaParams(credito.id, template.key);
      const data = res?.data?.data || {};
      setTemplateModal({ template, credito, loading: false, error: null, params: data.params || [], valores: data.valores || {} });
    } catch (error) {
      setTemplateModal({ template, credito, loading: false, error: errorApi(error, 'No se pudo preparar la plantilla.'), params: [], valores: {} });
    }
  };

  const ejecutarTemplate = async (template, telefono = null, credito = selectedCredito, params = []) => {
    if (sending) return;
    // Las plantillas sí necesitan contrato: nombre, monto y clave salen de él.
    if (!credito) {
      setSendError('Esta conversación no tiene contrato asociado; las plantillas requieren uno. Puedes responder con texto libre si la ventana está abierta.');
      return;
    }
    if (!params.length && (template.parametros || []).length) {
      setSendError('El backend no pudo calcular los datos de esta plantilla.');
      return;
    }

    setSending(true);
    setSendError(null);
    try {
      let respuesta;
      if (selected.id) {
        respuesta = await carteraApi.enviarConversacionMensaje(selected.id, {
          tipo: 'plantilla',
          template: template.key,
          params,
          credito_id: credito.id,
        });
        agregarMensajeRespuesta(respuesta);
        requestAnimationFrame(() => scrollAlFinal());
      } else {
        respuesta = await carteraApi.enviarWhatsappPlantilla(credito.id, {
          template: template.key,
          params,
          ...(telefono ? { telefono } : {}),
        });
        await cargarLista(() => { }, undefined, true);
        const conversacionId = respuesta?.data?.conversacion_id;
        if (conversacionId) {
          const key = String(conversacionId);
          setSelectedKey(key);
          if (onSelectConversacion) onSelectConversacion(key);
        }
      }
      setTemplatePendiente(null);
      setTemplateModal(null);
    } catch (error) {
      agregarMensajeRespuesta(error?.response);
      const conversacionId = error?.response?.data?.conversacion_id;
      if (!selected.id && conversacionId) {
        await cargarLista(() => { }, undefined, true);
        const key = String(conversacionId);
        setSelectedKey(key);
        if (onSelectConversacion) onSelectConversacion(key);
      }
      setSendError(errorApi(error, 'No se pudo enviar la plantilla.'));
    } finally {
      setSending(false);
    }
  };

  const enviarTemplate = (template) => {
    if (selected?.estado && selected.estado !== 'activa') {
      setSendError(estadoBloqueo[selected.estado] || 'La conversación no está activa.');
      return;
    }
    prepararTemplate(template);
  };

  const guardarTelefonoYEnviar = () => {
    if (!telefonoInput.trim()) {
      setSendError('Sin teléfono — captúralo para poder enviar.');
      return;
    }
    if (templatePendiente) ejecutarTemplate(
      templatePendiente,
      telefonoInput.trim(),
      templateModal?.credito || selectedCredito,
      templateModal?.params || [],
    );
  };

  const enviarTexto = async () => {
    const texto = draft.trim();
    if (!selected?.id || !texto || sending) return;
    setSending(true);
    setSendError(null);
    try {
      // credito_id es opcional en el backend: un teléfono puede no tener
      // contrato asociado (prueba interna, número nuevo) y aun así responderse.
      const respuesta = await carteraApi.enviarConversacionMensaje(selected.id, {
        tipo: 'texto',
        body: texto,
        ...(selectedCredito ? { credito_id: selectedCredito.id } : {}),
      });
      agregarMensajeRespuesta(respuesta);
      setDraft('');
      requestAnimationFrame(() => scrollAlFinal());
    } catch (error) {
      agregarMensajeRespuesta(error?.response);
      setSendError(errorApi(error, 'No se pudo enviar el mensaje.'));
    } finally {
      setSending(false);
    }
  };

  const tomarConversacion = async () => {
    if (!selected?.id) return;
    setSendError(null);
    try {
      const res = await carteraApi.marcarConversacionLeida(selected.id, { tomar: true });
      const modo = res?.data?.data?.modo || 'humano';
      setConversaciones((actuales) => actuales.map((fila) => (
        fila.id === selected.id ? { ...fila, modo } : fila
      )));
    } catch (error) {
      setSendError(errorApi(error, 'No se pudo tomar la conversación.'));
    }
  };

  // ── Alertas de los botones ────────────────────────────────────────────
  const alertas = useMemo(() => selected?.alertas || [], [selected]);
  // La de "dice que pagó" es la única accionable desde aquí (las otras son
  // avisos: el cobrador contesta o manda el PDF y con eso se cierran).
  /*
   * TODAS las de pago, no solo la primera. Antes era un .find(): con un cliente
   * de 3 contratos se pintaba UNA tarjeta y las demás quedaban invisibles — el
   * contador resolvía esa, veía el panel limpio, y se perdía los otros reportes
   * sin enterarse de que existían. Ordenadas por monto, el más alto arriba.
   */
  const alertasPago = useMemo(
    () => alertas
      .filter((a) => a.motivo === 'ya_pague')
      .slice()
      .sort((a, b) => Number(b.monto_actual ?? b.monto ?? 0) - Number(a.monto_actual ?? a.monto ?? 0)),
    [alertas],
  );

  const refrescarLista = () => cargarLista(() => { }, undefined, true);

  /*
   * Refresco manual de LAS DOS cosas a la vez (lista + hilo abierto). Sustituye
   * al polling mientras está apagado. Silencioso a propósito: pasar un setter
   * de loading real desmonta el panel y se pierde la pestaña activa.
   */
  const refrescarTodo = async () => {
    if (refrescando) return;
    setRefrescando(true);
    try {
      await Promise.all([
        cargarLista(() => { }, undefined, true),
        selected?.id
          ? cargarMensajes(selected.id, () => { }, undefined, { silencioso: true })
          : Promise.resolve(),
      ]);
    } finally {
      setRefrescando(false);
    }
  };

  const resolverAlerta = async (gestion, resultado) => {
    if (!gestion?.id || resolviendo) return;
    setResolviendo(gestion.id);
    setSendError(null);
    setAviso(null);
    try {
      const res = await carteraApi.resolverGestion(gestion.id, { resultado });
      setAviso(res?.data?.message || 'Alerta resuelta.');
      await refrescarLista();
    } catch (error) {
      setSendError(errorApi(error, 'No se pudo resolver la alerta.'));
      // La tarjeta pudo quedar obsoleta (el pago se registró desde Cartera y
      // cerró la gestión). Refrescar deja la lista igual que la base.
      await refrescarLista();
    } finally {
      setResolviendo(null);
    }
  };

  const reverificarAlerta = async (gestion) => {
    if (!gestion?.id || resolviendo) return;
    setResolviendo(gestion.id);
    setSendError(null);
    setAviso(null);
    try {
      const res = await carteraApi.reverificarGestion(gestion.id);
      setAviso(res?.data?.message || 'Verificación terminada.');
      await refrescarLista();
    } catch (error) {
      setSendError(errorApi(error, 'No se pudo volver a consultar el pago.'));
      await refrescarLista();
    } finally {
      setResolviendo(null);
    }
  };

  const registrarPagoDeAlerta = async (gestion) => {
    const id = gestion?.credito_id || selectedCredito?.id;
    if (!id || pagoModalLoading) return;
    setPagoModalLoading(true);
    setSendError(null);
    try {
      const res = await carteraApi.getCredito(id);
      const detalle = res?.data?.data || {};
      const credito = detalle.resumen || {};
      const cuenta = detalle.estado_de_cuenta || [];
      const pendientesPago = cuenta.filter((fila) => !['paid', 'pagado', 'cancelled', 'waived'].includes(fila.estatus));
      const restante = (fila) => Math.max(0, Number(fila.pago_total || 0) - Number(fila.monto_pagado || 0));
      const mora = Number(credito.mora_acumulada || 0);
      const vencidas = pendientesPago.filter((fila) => fila.fecha_vencimiento && new Date(`${fila.fecha_vencimiento}T00:00:00`) < new Date());
      setPagoModal({
        credito,
        proximaMensualidad: Number(pendientesPago[0]?.pago_total || 0),
        completarCuota: restante(pendientesPago[0] || {}),
        alCorriente: vencidas.reduce((sum, fila) => sum + restante(fila), 0) + mora,
        maxPagar: pendientesPago.reduce((sum, fila) => sum + restante(fila), 0) + mora,
      });
    } catch (error) {
      setSendError(errorApi(error, 'No se pudo abrir el registro de pago.'));
    } finally {
      setPagoModalLoading(false);
    }
  };

  const ejecutarAviso = async (key, action, fallback) => {
    setAvisosBusy(key);
    setAvisosNotice(null);
    try {
      const res = await action();
      setAvisosNotice({ type: res?.data?.status === 'partial' ? 'warn' : 'ok', text: res?.data?.message || 'Listo.' });
      await cargarAvisos();
    } catch (error) {
      setAvisosNotice({ type: 'error', text: errorApi(error, fallback) });
    } finally {
      setAvisosBusy('');
    }
  };

  const cambiarModoAvisos = (modo) => {
    if (avisosBusy || modo === avisosData?.modo) return;
    ejecutarAviso(`modo-${modo}`, () => carteraApi.actualizarModoAvisos(modo), 'No se pudo cambiar el modo.');
  };

  // ── Adjuntos ──────────────────────────────────────────────────────────
  const subirDocumento = async (archivo, caption = null, requestId = null) => {
    if (!selected?.id || !archivo) return null;
    setSending(true);
    setSendError(null);
    setAviso(null);
    try {
      const fd = new FormData();
      fd.append('archivo', archivo);
      if (selectedCredito) fd.append('credito_id', selectedCredito.id);
      if (caption) fd.append('caption', caption);
      // Idempotencia del intento: sin esto, un doble clic o un reintento de axios
      // le manda al cliente el mismo documento dos veces.
      if (requestId) fd.append('request_id', requestId);
      const respuesta = await carteraApi.enviarConversacionDocumento(selected.id, fd);
      agregarMensajeRespuesta(respuesta);
      requestAnimationFrame(() => scrollAlFinal());
      return respuesta;
    } catch (error) {
      agregarMensajeRespuesta(error?.response);
      setSendError(errorApi(error, 'No se pudo enviar el documento.'));
      // Se propaga: quien llamó necesita saber que falló para no cantar éxito.
      throw error;
    } finally {
      setSending(false);
    }
  };

  const elegirArchivo = (event) => {
    const archivo = event.target.files?.[0];
    event.target.value = ''; // permite volver a elegir el mismo archivo
    if (archivo) subirDocumento(archivo).catch(() => { });
  };

  /*
   * Genera el PDF del estado de cuenta EN EL NAVEGADOR (react-pdf) y lo manda
   * por el hilo sin que el cobrador tenga que descargarlo antes. Reusa
   * edoCuenta.js, el mismo constructor que usa la pestaña Estados de cuenta:
   * el cliente recibe exactamente el documento que ve el cobrador.
   */
  const enviarEstadoCuentaPdf = async () => {
    if (!selected?.id || !selectedCredito || pdfBusy) return;
    setPdfBusy(true);
    setSendError(null);
    setAviso(null);
    try {
      const res = await carteraApi.getCredito(selectedCredito.id);
      let liquid = null;
      // La liquidación anticipada solo existe en AF/CS. En arrendamiento puro
      // el backend responde 422 a propósito, así que ni se pregunta: evita una
      // petición inútil y un error rojo en la consola que parece un bug.
      if (selectedCredito.tipo !== 'arrendamiento_puro') {
        try {
          const lq = await carteraApi.liquidacionPreview(selectedCredito.id);
          liquid = lq?.data?.data || null;
        } catch (e) { /* sin bloque de liquidación */ }
      }

      const vista = buildEdoCuentaView(res?.data?.data || null, liquid);
      if (!vista) {
        setSendError('No se pudo armar el estado de cuenta de este contrato.');
        return;
      }
      const blob = await pdf(<EstadoCuentaPDF data={buildEdoCuentaPdfData(vista, true)} />).toBlob();
      const archivo = new File([blob], `estado-cuenta-${vista.r.clave_contrato}.pdf`, { type: 'application/pdf' });
      // El mensaje de error específico ya lo puso subirDocumento; sin este catch,
      // el `catch` de abajo lo sobreescribiría con el genérico de generación.
      await subirDocumento(archivo, `Estado de cuenta ${vista.r.clave_contrato}`).catch(() => { });
    } catch (error) {
      setSendError(errorApi(error, 'No se pudo generar el estado de cuenta.'));
    } finally {
      setPdfBusy(false);
    }
  };

  const cargarAnteriores = async () => {
    if (!selected?.id || !hasMore || olderLoading || mensajes.length === 0) return;
    const panel = chatBodyRef.current;
    const alturaAntes = panel?.scrollHeight || 0;
    setOlderLoading(true);
    try {
      const res = await carteraApi.getConversacionMensajes(selected.id, {
        before: mensajes[0].id,
      });
      const anteriores = [...(res?.data?.data || [])].reverse();
      mezclarMensajes(anteriores);
      setHasMore(Boolean(res?.data?.pagination?.has_more));
      requestAnimationFrame(() => {
        if (panel) panel.scrollTop += panel.scrollHeight - alturaAntes;
      });
    } catch (error) {
      setMensajesError(errorApi(error, 'No se pudo cargar el historial anterior.'));
    } finally {
      setOlderLoading(false);
    }
  };

  const ventanaHasta = fechaValida(selected?.ventana_abierta_hasta);
  const ventanaAbierta = Boolean(selected?.id && ventanaHasta && ventanaHasta.getTime() > Date.now());
  const horasVentana = ventanaAbierta
    ? Math.max(1, Math.ceil((ventanaHasta.getTime() - Date.now()) / 3600000))
    : 0;
  const bloqueada = Boolean(selected?.id && selected.estado !== 'activa');
  const textoHabilitado = ventanaAbierta && !bloqueada;
  const stage = stageStyle(selectedCredito?.etapa_cobranza, selectedCredito?.estatus);
  const prod = prodCorto(selectedCredito?.tipo);
  const totalRegularizar = creditos.reduce((total, credito) => (
    total + Number(credito.saldo_vencido || 0) + Number(credito.mora || 0)
  ), 0);
  const modoHasta = fechaValida(selected?.modo_humano_hasta);
  const horasModoHumano = selected?.modo === 'humano' && modoHasta
    ? Math.max(1, Math.ceil((modoHasta.getTime() - Date.now()) / 3600000))
    : 0;
  const totalAvisos = (avisosData?.grupos || []).reduce((total, grupo) => total + grupo.filas.length, 0);
  const templatePorNombre = useMemo(() => {
    const mapa = new Map();
    templates.forEach((template) => {
      mapa.set(template.name, template.label);
      mapa.set(template.key, template.label);
    });
    return mapa;
  }, [templates]);

  return (
    <div
      className="co-fade dc-chat-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: '296px minmax(0,1fr) 276px',
        gap: 18,
        alignItems: 'start',
        height: 'calc(100vh - 250px)',
        minHeight: 520,
      }}
    >
      <div className="co-card dc-chat-list" style={{ overflow: 'hidden', height: '100%' }}>
        <div style={{ padding: '13px 13px 0' }}>
          <div style={{ display: 'flex', gap: 4, background: '#F1F5F9', borderRadius: 11, padding: 3 }}>
            {[
              { key: 'cobranza', label: 'Cobranza', icon: 'pi-wallet' },
              { key: 'cotizaciones', label: 'Cotizaciones', icon: 'pi-car' },
            ].map((b) => {
              const activa = bandeja === b.key;
              const pend = contadores[b.key] || 0;
              return (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => { if (!activa) { setBandeja(b.key); setSelectedKey(null); } }}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    border: 'none', cursor: activa ? 'default' : 'pointer',
                    borderRadius: 9, padding: '7px 6px', fontSize: 12.5,
                    fontWeight: activa ? 700 : 600,
                    background: activa ? '#fff' : 'transparent',
                    color: activa ? '#0F172A' : '#64748B',
                    boxShadow: activa ? '0 1px 3px rgba(15,23,42,.10)' : 'none',
                  }}
                >
                  <span className={`pi ${b.icon}`} style={{ fontSize: 12 }} />
                  {b.label}
                  {pend > 0 && (
                    <span style={{ background: '#DC2626', color: '#fff', borderRadius: 999, fontSize: 10, fontWeight: 700, minWidth: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>
                      {pend}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        {/* Switch global del bot de ventas. Va junto al selector de bandeja
            (§4.4) y SOLO en cotizaciones: en cobranza no hay bot de ventas que
            apagar. El componente se monta/desmonta con la pestaña, así que su
            fetch de estado solo pega al backend cuando el operador está aquí. */}
        {bandeja === 'cotizaciones' && (
          <div style={{ padding: '0 13px', marginTop: 10, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <BotVentasSwitch />
            </div>
            <BotAjustesEngrane />
          </div>
        )}
        <div style={{ padding: 13 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: '#fff', border: '1.5px solid #34C6F1', borderRadius: 12, padding: '9px 12px', boxShadow: '0 0 0 3px rgba(52,198,241,.08)' }}>
            <span className="pi pi-search" style={{ color: '#32B8DF', fontSize: 13 }} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar conversación…"
              style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, width: '100%', color: '#0F172A' }}
            />
          </div>
        </div>

        {/* 65px del buscador + 47 del selector de bandeja */}
        <div style={{ height: 'calc(100% - 112px)', overflowY: 'auto' }}>
          {listLoading && lista.length === 0 && <ListaSkeleton />}
          {listError && lista.length === 0 && (
            <div style={{ padding: '34px 18px', textAlign: 'center', color: '#DC2626', fontSize: 12.5 }}>
              <span className="pi pi-exclamation-triangle" style={{ display: 'block', fontSize: 20, marginBottom: 9 }} />
              {listError}
            </div>
          )}
          {!listLoading && !listError && lista.length === 0 && (
            <div style={{ padding: '38px 18px', textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>
              {query
                ? 'Sin resultados'
                : bandeja === 'cotizaciones'
                  ? 'Todavía no hay cotizaciones por WhatsApp. Aquí van a caer los leads que cotizan en la página.'
                  : 'Sin resultados'}
            </div>
          )}
          {lista.map((fila) => {
            const st = stageStyle(fila.etapa_cobranza, fila.estatus);
            const estaSeleccionada = String(fila.key ?? fila.id) === String(selectedKey);
            const p = prodCorto(fila.tipo);
            return (
              <div
                key={fila.key ?? fila.id}
                // Lo usa enfocarFila() para traer la tarjeta a la vista cuando
                // la selección se reengancha sola tras una fusión.
                data-fila-key={String(fila.key ?? fila.id)}
                onClick={() => seleccionar(fila)}
                className="co-row-hover"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 11,
                  padding: '11px 15px',
                  borderTop: '1px solid #F4F7F9',
                  cursor: 'pointer',
                  background: estaSeleccionada ? '#EFF9FD' : '#fff',
                  borderLeft: `3px solid ${estaSeleccionada ? '#32B8DF' : 'transparent'}`,
                }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 10, background: st.bg, color: st.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                  {fila.iniciales || iniciales(fila.cliente)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fila.cliente}</span>
                    <span className="tnum" style={{ color: '#94A3B8', fontSize: 10.5 }}>{tiempoLista(fila.ultimo_mensaje_at)}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                    <span style={{ flex: 1, minWidth: 0, color: fila.sin_conversacion && !fila.telefono_e164 ? '#DC2626' : '#64748B', fontSize: 11.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fila.ultimo_mensaje}</span>
                    {fila.no_leidos > 0 && (
                      <span className="tnum" style={{ background: '#22EC9A', color: '#06241a', borderRadius: 999, fontSize: 10.5, fontWeight: 700, padding: '1px 7px' }}>{fila.no_leidos}</span>
                    )}
                  </div>
                  {/* Las alertas de los botones van ARRIBA de la etapa y con
                      más peso visual que el contador de no leídos: son lo
                      único de esta lista que exige una acción concreta. */}
                  {(fila.alertas?.length > 0 || fila.revision) && (
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 5 }}>
                      {(fila.alertas || []).slice(0, 2).map((al) => {
                        const meta = alertaMeta(al.motivo);
                        return (
                          <span
                            key={al.id}
                            className="dc-alert-badge"
                            style={{ background: meta.bg, borderColor: meta.borde, color: meta.color }}
                          >
                            {meta.corto}
                          </span>
                        );
                      })}
                      {fila.revision && (
                        <span className="dc-alert-badge" style={{ background: '#FFF7F7', borderColor: '#F3B5B5', color: '#C81E1E' }}>
                          ⚠ Revisar
                        </span>
                      )}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}>
                    <span style={{ fontSize: 9.5, fontWeight: 700, color: st.color, background: st.bg, padding: '2px 7px', borderRadius: 6 }}>{etapaLabelDe(fila.etapa_cobranza, fila.estatus)}</span>
                    <span className="tnum" style={{ minWidth: 0, fontSize: 10.5, color: '#94A3B8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <span style={{ color: (PROD[p] || PROD.AP).color, fontWeight: 700 }}>{p}</span> · {fila.clave_contrato}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="co-card dc-chat-main" style={{ overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {!selected && (
          <div style={{ margin: 'auto', padding: '60px 24px', textAlign: 'center' }}>
            <div style={{ width: 72, height: 72, borderRadius: 22, background: 'linear-gradient(135deg,#EAF6FB,#E5F8EF)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <span className="pi pi-comments" style={{ fontSize: 30, color: '#32B8DF' }} />
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0F172A', marginBottom: 6 }}>Selecciona una conversación</div>
            <div style={{ fontSize: 13.5, color: '#64748B' }}>Elige un cliente para consultar el hilo o iniciar uno con plantilla.</div>
          </div>
        )}

        {selected && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '13px 16px', borderBottom: '1px solid #EEF2F5' }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: stage.bg, color: stage.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                {iniciales(selected.cliente)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selected.cliente}</div>
                <div className="tnum dc-chat-subtitle">
                  <span>{(PROD[prod] || PROD.AP).name} · {selectedCredito?.clave_contrato || '—'} · {selected.telefono_e164 || 'sin teléfono'}</span>
                  {/* Corregir el número sin salir del hilo: el que se equivocó
                      al capturarlo lo nota justo aquí, viendo que no llega nada. */}
                  {selectedCredito && (
                    <button
                      type="button"
                      className="dc-edit-phone"
                      title={selectedCredito.sin_telefono ? 'Capturar teléfono' : 'Cambiar el teléfono de este contrato'}
                      onClick={() => setTelefonoModal(selectedCredito)}
                    >
                      <i className={`pi ${selectedCredito.sin_telefono ? 'pi-phone' : 'pi-pencil'}`} />
                    </button>
                  )}
                </div>
              </div>
              <span style={{ fontSize: 10.5, fontWeight: 700, color: selected.modo === 'humano' ? '#1f6f8c' : '#64748B', background: selected.modo === 'humano' ? '#EAF7FC' : '#EEF1F4', padding: '4px 9px', borderRadius: 999 }}>
                {selected.modo === 'humano' ? 'Humano' : selected.modo === 'pausado' ? 'Pausado' : 'Bot'}
              </span>
              {horasModoHumano > 0 && <span className="dc-mode-until">avisos en {horasModoHumano} h</span>}
              {/* Toggle Bot/Humano por conversación (§4.2). SOLO en cotizaciones:
                  los hilos de cobranza no se tocan desde aquí — su `modo` es de
                  su cobrador. El refresco tras reactivar reusa refrescarTodo, que
                  trae el estado real del hilo — nunca se adelanta la posición.

                  🔴 La condición es el TIPO del hilo, no que tenga historial del bot.
                  Antes era `Boolean(selected.bot_ventas)`, y `bot_ventas` solo existe
                  después de que el bot procesa un turno. Eso cerraba un círculo: el
                  hilo #16 de producción quedó en modo humano, el bot nunca lo tocó
                  justo por eso, y el control para desatorarlo estaba oculto porque no
                  había historial. La única salida era la consola. */}
              <ModoHiloToggle
                conversacion={selected}
                esCotizacion={selected.tipo === 'cotizacion' || Boolean(selected.bot_ventas)}
                pausado={Boolean(selected.bot_ventas?.pausado)}
                modo={selected.modo}
                onReactivado={refrescarTodo}
              />
              {/* Refresco manual de lista + hilo: sustituye al polling apagado (POLL_ACTIVO). */}
              <button
                type="button"
                className="dc-refresh-button"
                onClick={refrescarTodo}
                disabled={refrescando}
                title="Actualizar conversaciones y mensajes"
              >
                <i className={`pi ${refrescando ? 'pi-spin pi-spinner' : 'pi-refresh'}`} />
                {refrescando ? 'Actualizando…' : 'Actualizar'}
              </button>
              <button type="button" className="dc-queue-button" onClick={() => { setColaOpen(true); cargarAvisos(); }}><i className="pi pi-send" /> Avisos <b>{totalAvisos}</b></button>
              {selected.id && selected.modo !== 'humano' && (
                <Pill icon="pi-user-plus" primary onClick={tomarConversacion} style={{ padding: '8px 12px', fontSize: 11.5 }}>Tomar</Pill>
              )}
            </div>

            <div className="dc-chat-contextbar">
              <span><i className="pi pi-calendar" />{selectedCredito?.proximo_recordatorio
                ? `${selectedCredito.proximo_recordatorio.plantilla} · ${selectedCredito.proximo_recordatorio.dias > 0 ? `en ${selectedCredito.proximo_recordatorio.dias} días` : selectedCredito.proximo_recordatorio.dias === 0 ? 'hoy' : 'vencido'}`
                : 'Sin próximo aviso programado'}</span>
              <span><i className="pi pi-exclamation-circle" />{selectedCredito?.cuotas_vencidas || 0} cuotas vencidas</span>
              {avisosData?.pausado && <button type="button" disabled={Boolean(avisosBusy)} onClick={() => ejecutarAviso('reanudar', () => carteraApi.actualizarPausaAvisos(false), 'No se pudieron reanudar los envíos.')}><i className="pi pi-play" /> Reanudar avisos</button>}
            </div>

            {bloqueada && (
              <div style={{ padding: '10px 16px', background: '#FBE7E7', borderBottom: '1px solid #F2CACA', color: '#B91C1C', fontSize: 12.5, fontWeight: 600 }}>
                <span className="pi pi-ban" style={{ marginRight: 7 }} />{estadoBloqueo[selected.estado] || 'La conversación no está activa.'}
              </div>
            )}

            <div
              ref={chatBodyRef}
              onScroll={(event) => {
                const panel = event.currentTarget;
                if (panel.scrollHeight - panel.scrollTop - panel.clientHeight < 72) setNuevos(false);
              }}
              style={{ position: 'relative', flex: 1, minHeight: 0, overflowY: 'auto', background: '#F4F8FB', display: 'flex', flexDirection: 'column', gap: 9, padding: '14px 16px' }}
            >
              {selected.id && hasMore && (
                <button type="button" className="dc-chat-more" onClick={cargarAnteriores} disabled={olderLoading}>
                  <span className={`pi ${olderLoading ? 'pi-spin pi-spinner' : 'pi-history'}`} /> Cargar anteriores
                </button>
              )}
              {selected.id && mensajesLoading && <MensajesSkeleton />}
              {mensajesError && !mensajesLoading && (
                <div style={{ alignSelf: 'center', color: '#DC2626', fontSize: 12.5, padding: 12 }}>{mensajesError}</div>
              )}
              {!selected.id && (
                <div style={{ margin: 'auto', textAlign: 'center', maxWidth: 340, padding: 24 }}>
                  <div style={{ width: 64, height: 64, borderRadius: 20, background: '#fff', border: '1px solid #EEF2F5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                    <span className="pi pi-whatsapp" style={{ fontSize: 27, color: '#22B573' }} />
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A' }}>Aún no hay conversación</div>
                  <div style={{ fontSize: 12.5, color: '#64748B', marginTop: 5 }}>Iníciala con una de las plantillas aprobadas.</div>
                </div>
              )}
              {selected.id && !mensajesLoading && mensajes.length === 0 && !mensajesError && (
                <div style={{ margin: 'auto', textAlign: 'center', color: '#64748B', fontSize: 13 }}>Este hilo todavía no tiene mensajes.</div>
              )}
              {mensajes.map((mensaje, index) => {
                const previo = mensajes[index - 1];
                const cambiaDia = !previo || diaClave(previo.created_at) !== diaClave(mensaje.created_at);
                const entrante = mensaje.direccion === 'in';
                const plantilla = mensaje.tipo === 'template';
                const esMedia = ['image', 'document', 'audio', 'sticker', 'video'].includes(mensaje.tipo);
                const fallido = mensaje.status === 'failed';
                const statusColor = mensaje.status === 'read' ? '#34B0E0' : '#94A3B8';
                return (
                  <React.Fragment key={mensaje.id}>
                    {cambiaDia && (
                      <div className="tnum" style={{ background: '#E4EBF1', color: '#64748B', fontSize: 11, fontWeight: 600, padding: '3px 12px', borderRadius: 999, alignSelf: 'center' }}>{diaLabel(mensaje.created_at)}</div>
                    )}
                    <div
                      onClick={plantilla ? () => setDetalleMensaje(mensaje) : undefined}
                      role={plantilla ? 'button' : undefined}
                      tabIndex={plantilla ? 0 : undefined}
                      onKeyDown={plantilla ? (event) => { if (event.key === 'Enter') setDetalleMensaje(mensaje); } : undefined}
                      style={{
                        maxWidth: plantilla ? '78%' : (esMedia ? '76%' : '64%'),
                        padding: plantilla ? '8px 12px' : '9px 13px',
                        borderRadius: plantilla ? 10 : 14,
                        border: fallido ? '1px solid #F0A7A7' : (entrante || plantilla ? `1px solid ${plantilla ? '#F4E2BC' : '#EEF2F5'}` : 'none'),
                        borderBottomLeftRadius: entrante && !plantilla ? 4 : undefined,
                        borderBottomRightRadius: !entrante && !plantilla ? 4 : undefined,
                        background: plantilla ? '#FFF6E5' : (entrante ? '#fff' : '#DCF8E8'),
                        color: plantilla ? '#8a6d2a' : '#1E293B',
                        alignSelf: plantilla ? 'center' : (entrante ? 'flex-start' : 'flex-end'),
                        textAlign: plantilla ? 'center' : 'left',
                        fontSize: plantilla ? 12.5 : 13.5,
                        lineHeight: 1.4,
                        overflowWrap: 'anywhere', cursor: plantilla ? 'pointer' : undefined,
                      }}
                    >
                      {plantilla ? (
                        <><span className="pi pi-file" style={{ fontSize: 11, marginRight: 5 }} />Plantilla · {templatePorNombre.get(mensaje.template_name) || mensaje.template_name || 'enviada'}</>
                      ) : (esMedia
                        ? <MediaMensaje conversacionId={selected.id} mensaje={mensaje} />
                        : (mensaje.body || `[${mensaje.tipo}]`))}
                      {mensaje.origen === 'cron' && (
                        <span style={{ display: 'inline-block', marginLeft: 7, background: '#EAF7FC', color: '#1f6f8c', borderRadius: 999, fontSize: 9.5, fontWeight: 700, padding: '1px 6px' }}>automático</span>
                      )}
                      <div className="tnum" style={{ marginTop: 4, textAlign: 'right', fontSize: 10, color: fallido ? '#DC2626' : '#94A3B8', fontWeight: 600 }}>
                        {horaDe(mensaje.created_at)}
                        {!entrante && !fallido && mensaje.status === 'queued' && <span style={{ marginLeft: 5 }}>· en cola</span>}
                        {!entrante && !fallido && mensaje.status === 'sent' && <span style={{ marginLeft: 5, color: statusColor }}>✓</span>}
                        {!entrante && !fallido && mensaje.status === 'delivered' && <span style={{ marginLeft: 5, color: statusColor }}>✓✓</span>}
                        {!entrante && !fallido && mensaje.status === 'read' && <span style={{ marginLeft: 5, color: statusColor }}>✓✓</span>}
                      </div>
                      {fallido && (
                        <div style={{ marginTop: 5, color: '#DC2626', fontSize: 11, fontWeight: 600 }}>
                          <span className="pi pi-times-circle" style={{ marginRight: 5 }} title={mensaje.error_message || ''} />No enviado: {errorEnvioLegible(mensaje.error_message)}
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
              {nuevos && (
                <button type="button" className="dc-chat-new" onClick={() => scrollAlFinal()}>
                  nuevos mensajes <span className="pi pi-arrow-down" />
                </button>
              )}
            </div>

            <div style={{ borderTop: '1px solid #E7EBF0', background: '#fff' }}>
              <div style={{ padding: '8px 16px 0', display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                <span style={{
                  background: ventanaAbierta ? '#E5F8EF' : '#FFF6E5',
                  color: ventanaAbierta ? '#087A4D' : '#9A681D',
                  border: `1px solid ${ventanaAbierta ? '#CBEEDD' : '#F4E2BC'}`,
                  borderRadius: 999,
                  fontSize: 10.5,
                  fontWeight: 700,
                  padding: '4px 9px',
                }}>
                  <span className={`pi ${ventanaAbierta ? 'pi-unlock' : 'pi-lock'}`} style={{ marginRight: 5, fontSize: 9 }} />
                  {ventanaAbierta ? `Ventana abierta · quedan ${horasVentana}h` : 'Ventana cerrada'}
                </span>
                {creditos.map((credito) => (
                  <button
                    key={`auto-${credito.id}`}
                    type="button"
                    className="dc-auto-chip"
                    disabled={sending || bloqueada || templates.length === 0}
                    title="Preparar una plantilla para este vehículo"
                    onClick={() => {
                      seleccionarCredito(credito.id);
                      prepararTemplate(templates[0], credito);
                    }}
                  >
                    <i className="pi pi-car" />{credito.vehiculo || credito.clave_contrato}
                  </button>
                ))}
                {templates.map((template) => (
                  <button
                    key={template.key}
                    type="button"
                    className="dc-template-chip"
                    disabled={sending || bloqueada}
                    onClick={() => enviarTemplate(template)}
                  >
                    {template.label}
                  </button>
                ))}
              </div>

              {selectedCredito?.sin_telefono && (
                <div style={{ margin: '9px 16px 0', padding: '10px 12px', border: '1px solid #F3B5B5', borderRadius: 12, background: '#FFF7F7' }}>
                  <div style={{ color: '#C81E1E', fontSize: 11.5, fontWeight: 700, marginBottom: 7 }}>Sin teléfono — captúralo para poder enviar</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      className="tnum"
                      value={telefonoInput}
                      onChange={(event) => setTelefonoInput(event.target.value)}
                      placeholder="Ej. 55 1234 5678"
                      style={{ flex: 1, minWidth: 0, border: '1.5px solid #DC2626', borderRadius: 10, padding: '8px 11px', outline: 'none', color: '#0F172A' }}
                    />
                    <button type="button" className="dc-send-phone" disabled={!templatePendiente || sending} onClick={guardarTelefonoYEnviar}>
                      {sending ? <span className="pi pi-spin pi-spinner" /> : <span className="pi pi-send" />}
                      {templatePendiente ? 'Guardar y enviar' : 'Elige plantilla'}
                    </button>
                  </div>
                </div>
              )}

              {sendError && (
                <div style={{ margin: '8px 16px 0', color: '#DC2626', fontSize: 11.5, fontWeight: 600 }}>
                  <span className="pi pi-exclamation-circle" style={{ marginRight: 5 }} />{sendError}
                </div>
              )}

              <div style={{ padding: '11px 16px 13px', display: 'flex', alignItems: 'flex-end', gap: 10 }}>
                {/* Fuera de la ventana de 24 h Meta no acepta media suelta:
                    el clip queda deshabilitado, con el motivo en el title. */}
                <input
                  ref={fileRef}
                  type="file"
                  accept="application/pdf"
                  style={{ display: 'none' }}
                  onChange={elegirArchivo}
                />
                <button
                  type="button"
                  className="dc-chat-clip"
                  disabled={!textoHabilitado || sending}
                  title={textoHabilitado ? 'Adjuntar un PDF' : 'Disponible solo con la ventana abierta'}
                  onClick={() => fileRef.current?.click()}
                >
                  <span className={`pi ${sending ? 'pi-spin pi-spinner' : 'pi-paperclip'}`} />
                </button>
                <textarea
                  value={draft}
                  disabled={!textoHabilitado}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') enviarTexto();
                  }}
                  placeholder={textoHabilitado ? 'Escribe un mensaje…' : 'Ventana cerrada — solo puedes enviar una plantilla'}
                  rows={1}
                  style={{ flex: 1, minHeight: 40, maxHeight: 96, resize: 'vertical', border: '1.5px solid #E7EBF0', borderRadius: 18, padding: '10px 15px', outline: 'none', fontFamily: 'inherit', fontSize: 13.5, color: '#0F172A', background: textoHabilitado ? '#fff' : '#F7FAFC' }}
                />
                <button type="button" className="dc-chat-send" disabled={!textoHabilitado || !draft.trim() || sending} onClick={enviarTexto}>
                  <span className={`pi ${sending ? 'pi-spin pi-spinner' : 'pi-send'}`} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="dc-chat-context dc-chat-context-panel">
        {selected && selectedCredito && (
          <section className="dc-context-client">
            <div className="dc-context-client-head">
              <div className="dc-context-avatar" style={{ background: stage.bg, color: stage.color }}>{iniciales(selected.cliente)}</div>
              <div>
                <b>{selected.cliente}</b>
                <span>{creditos.length} {creditos.length === 1 ? 'contrato' : 'contratos'}</span>
              </div>
            </div>
            <div className={`dc-context-debt ${totalRegularizar > 0 ? 'danger' : 'clear'}`}>
              {totalRegularizar > 0 ? (
                <>
                  <span>Total a regularizar</span>
                  <strong className="tnum">{money(totalRegularizar)}</strong>
                  <small>Saldo vencido y mora de todos sus contratos</small>
                </>
              ) : (
                <><i className="pi pi-check-circle" /><b>Sin saldo vencido</b></>
              )}
            </div>
          </section>
        )}

        {/* Arriba de TODO: lo que el cliente pidió desde un botón. El usuario
            la quiere imposible de ignorar, no escondida entre los saldos. */}
        {alertasPago.length > 0 && (
          <div className="dc-payment-alerts">
            <div className="dc-payment-alerts-head">
              <span><i className="pi pi-exclamation-triangle" /></span>
              <div>
                <b>{alertasPago.length} {alertasPago.length === 1 ? 'pago por verificar' : 'pagos por verificar'}</b>
                <small>El cliente tocó “Ya pagué” y no aparece en la base</small>
              </div>
            </div>
            {alertasPago.map((alertaPago) => {
              const abierta = String(alertaAbiertaId) === String(alertaPago.id);
              // La alerta guarda la MENSUALIDAD; los moratorios van aparte.
              const cuotaAlerta = Number(alertaPago.monto_actual ?? alertaPago.monto ?? 0);
              const moraAlerta = Number(alertaPago.mora_actual ?? 0);
              return (
                <div className={`dc-payment-alert ${abierta ? 'open' : ''}`} key={alertaPago.id}>
                  <button type="button" className="dc-payment-alert-summary" aria-expanded={abierta} onClick={() => setAlertaAbiertaId(abierta ? null : alertaPago.id)}>
                    <span className="dc-payment-alert-badge"><i className={alertaPago.cubierta ? 'pi pi-check' : 'pi pi-exclamation'} /></span>
                    <span>
                      <b>{alertaPago.vehiculo || alertaPago.clave_contrato || 'Pago reportado'}</b>
                      <small className="tnum">{alertaPago.clave_contrato || 'Sin contrato'} · {fechaHoraCorta(alertaPago.created_at)}</small>
                    </span>
                    <span className="dc-alert-amount">
                      <strong className="tnum">{money(cuotaAlerta + moraAlerta)}</strong>
                      <small>{moraAlerta > 0 ? 'con moratorios' : 'sin moratorios'}</small>
                    </span>
                    <i className={`pi ${abierta ? 'pi-chevron-up' : 'pi-chevron-down'}`} />
                  </button>
                  {abierta && (
                    <div className="dc-payment-alert-detail">
                      <div className="dc-alert-row"><span>Reportado</span><b>{fechaHoraCorta(alertaPago.created_at)}</b></div>
                      <div className="dc-alert-row"><span>Cuota</span><b>{alertaPago.fecha_vencimiento ? fmtDate(alertaPago.fecha_vencimiento) : '—'}</b></div>
                      <div className="dc-alert-row"><span>Sistema</span><b className={alertaPago.cubierta ? 'ok' : 'error'}>{alertaPago.cubierta ? 'PAGO REGISTRADO' : (alertaPago.cuota_vigente === false ? 'LA CUOTA YA NO EXISTE' : 'SIN PAGO REGISTRADO')}</b></div>
                      {/* Desglose explícito: el encabezado da el total y aquí se ve de qué se compone. */}
                      <div className="dc-alert-row"><span>Mensualidad</span><b>{money(cuotaAlerta)}</b></div>
                      <div className="dc-alert-row">
                        <span>Moratorios</span>
                        <b className={moraAlerta > 0 ? 'error' : 'ok'}>{moraAlerta > 0 ? money(moraAlerta) : 'Sin moratorios'}</b>
                      </div>
                      <div className="dc-alert-row dc-alert-total">
                        <span>Total para ponerse al corriente</span>
                        <b>{money(cuotaAlerta + moraAlerta)}</b>
                      </div>
                      {alertaPago.expirada && <div className="dc-alert-nota">Pasó el plazo de revisión: los recordatorios automáticos ya se reanudaron.</div>}
                      <div className="dc-alert-acciones">
                        <button type="button" className="dc-alert-btn primary" disabled={pagoModalLoading || resolviendo === alertaPago.id} onClick={() => registrarPagoDeAlerta(alertaPago)}><span className={`pi ${pagoModalLoading ? 'pi-spin pi-spinner' : 'pi-wallet'}`} /> Registrar pago</button>
                        <button type="button" className="dc-alert-btn" disabled={resolviendo === alertaPago.id} onClick={() => reverificarAlerta(alertaPago)}><span className={`pi ${resolviendo === alertaPago.id ? 'pi-spin pi-spinner' : 'pi-refresh'}`} /> Ya está pagado</button>
                        {!alertaPago.cubierta && <button type="button" className="dc-alert-btn" disabled={resolviendo === alertaPago.id} onClick={() => resolverAlerta(alertaPago, 'no_pagado')}><span className={`pi ${resolviendo === alertaPago.id ? 'pi-spin pi-spinner' : 'pi-times'}`} /> No ha pagado</button>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Los otros botones no exigen decisión, solo que alguien conteste. */}
        {alertas.filter((al) => al.motivo !== 'ya_pague').map((al) => {
          const meta = alertaMeta(al.motivo);
          return (
            <div
              key={al.id}
              className="dc-alert-aviso"
              style={{ background: meta.bg, borderColor: meta.borde, color: meta.color }}
            >
              <div>
                <span className={`pi ${meta.icon}`} style={{ marginRight: 7 }} />
                {meta.label} · {fechaHoraCorta(al.created_at)}
                {al.reportes > 1 && <> · lo pidió {al.reportes} veces</>}
              </div>
              {/* A quién se avisó y si salió. Antes solo decía "Pidió asesor" y
                  no había forma de saber si alguien se enteró. */}
              {avisosTexto(al.avisos).map((linea) => (
                <div key={linea.k} style={{ fontSize: 11, fontWeight: 600, opacity: .85, marginTop: 4 }}>
                  <span className={`pi ${linea.icon}`} style={{ marginRight: 6, fontSize: 10 }} />{linea.txt}
                </div>
              ))}
            </div>
          );
        })}

        {selected?.revision && (
          <div className="dc-alert-aviso" style={{ background: '#FFF7F7', borderColor: '#F3B5B5', color: '#C81E1E' }}>
            <span className="pi pi-flag" style={{ marginRight: 7 }} />
            {selected.revision.motivo === 'contrato_recuperado'
              ? 'Este cliente tiene un contrato cerrado por recuperación de auto. El bot de ventas no le contesta: atiéndelo tú.'
              : `El sistema no supo qué hacer con el último mensaje (${selected.revision.motivo}). Revísalo y toma la conversación.`}
          </div>
        )}

        {aviso && (
          <div className="dc-alert-aviso" style={{ background: '#E5F8EF', borderColor: '#CBEEDD', color: '#087A4D' }}>
            <span className="pi pi-check-circle" style={{ marginRight: 7 }} />{aviso}
          </div>
        )}

        {!selected && (
          <div style={{ color: '#94A3B8', fontSize: 12.5, textAlign: 'center', marginTop: 36 }}>El contexto del contrato aparecerá aquí.</div>
        )}
        {/* Hilo sin contrato: el teléfono no coincide con ninguno de cartera
            (número nuevo, prueba interna, o el cliente escribe desde otra línea).
            Antes esta card quedaba COMPLETAMENTE vacía y parecía un bug. */}
        {selected && !selectedCredito && (
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <div style={{
              width: 72, height: 72, borderRadius: 22, margin: '0 auto 14px',
              background: 'linear-gradient(135deg,#EAF6FB,#E5F8EF)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span className="pi pi-user" style={{ fontSize: 26, color: '#32B8DF' }} />
            </div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }}>{selected.cliente}</div>
            {selected.telefono_e164 && (
              <div className="tnum" style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>+{selected.telefono_e164}</div>
            )}
            {/* Lead de landing: en vez del aviso de "no hay contrato" —que aquí
                no es un problema sino lo esperado— se muestra QUÉ cotizó, que
                es lo único que el asesor necesita para atenderlo. */}
            {selected.cotizacion ? (
              <div style={{
                marginTop: 14, padding: '12px 13px', borderRadius: 12,
                background: '#F4FBFE', border: '1px solid #CDEBF7', textAlign: 'left',
              }}>
                {/* 🔴 `cotizacion` es la de la LANDING y no se reescribe nunca: tras
                    una negociación miente. `cotizacion_actual` trae la recotización
                    aplicada, y cae a la original cuando no hay ninguna o cuando la
                    última no se pudo consolidar. */}
                <div style={{ fontSize: 10.5, fontWeight: 700, color: '#32B8DF', letterSpacing: .3, textTransform: 'uppercase', marginBottom: 8 }}>
                  {selected.cotizacion_actual?.fuente === 'recotizacion_aplicada'
                    ? 'Cotización vigente · renegociada'
                    : 'Cotizó en la página'}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', marginBottom: 9 }}>
                  {selected.cotizacion.auto}
                </div>
                {[
                  ['Plazo', selected.cotizacion_actual?.plazo || selected.cotizacion.plazo],
                  // Copy inequívoco: el número es el TOTAL con IVA, no el enganche.
                  ['Desembolso inicial (IVA incl.)', selected.cotizacion_actual?.pago_inicial || selected.cotizacion.pago_inicial],
                  ['Renta mensual', selected.cotizacion_actual?.renta || selected.cotizacion.renta],
                  ['Folio', selected.cotizacion.folio],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 11.5, padding: '3px 0' }}>
                    <span style={{ color: '#64748B' }}>{k}</span>
                    <b className="tnum" style={{ color: '#0F172A' }}>{v}</b>
                  </div>
                ))}

                {selected.cotizacion_actual?.pendiente_de_consolidar && (
                  <div style={{
                    marginTop: 9, padding: '8px 10px', borderRadius: 9,
                    background: '#FFF6E5', border: '1px solid #F4E2BC',
                    color: '#8a6d2a', fontSize: 11, fontWeight: 600, lineHeight: 1.35,
                  }}>
                    La última recotización está pendiente de consolidar: estas cifras
                    son las originales y todavía no se puede enviar el PDF.
                  </div>
                )}

                <CambioCotizacionAviso
                  conversacionId={selected.id}
                  onAvisado={refrescarTodo}
                />

                <CotizacionPdfAction
                  conversacion={selected}
                  habilitado={Boolean(features.cotizacion_pdf_manual)}
                  ventanaAbierta={ventanaAbierta}
                  bloqueada={bloqueada}
                  ocupado={sending || pdfBusy}
                  onEnviar={subirDocumento}
                />
              </div>
            ) : (
              <div style={{
                marginTop: 14, padding: '11px 13px', borderRadius: 12,
                background: '#FFF6E5', border: '1px solid #F4E2BC',
                color: '#8a6d2a', fontSize: 11.5, fontWeight: 600, textAlign: 'left',
              }}>
                Este número no está ligado a ningún contrato de cartera, así que no
                hay saldos que mostrar. Puedes responderle con texto libre mientras
                la ventana de 24 h esté abierta, pero no enviarle plantillas de cobranza.
              </div>
            )}
          </div>
        )}
        {selected && selectedCredito && (
          <div className="dc-context-stack">
            <section className="dc-context-contracts">
              <div className="dc-context-section-title">
                <b>Contratos</b>
                <span>toca uno para trabajarlo</span>
              </div>
              <div className="dc-contract-list">
                {creditos.map((credito) => {
                  const activo = String(credito.id) === String(selectedCredito.id);
                  const etapa = stageStyle(credito.etapa_cobranza, credito.estatus);
                  const alCorriente = Number(credito.saldo_vencido || 0) + Number(credito.mora || 0) <= 0;
                  const producto = prodCorto(credito.tipo);
                  return (
                    <article
                      className={`dc-contract-card ${activo ? 'selected' : ''}`}
                      key={credito.id}
                      role="button"
                      tabIndex={0}
                      aria-pressed={activo}
                      onClick={() => seleccionarCredito(credito.id)}
                      onKeyDown={(event) => {
                        if (event.target !== event.currentTarget) return;
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          seleccionarCredito(credito.id);
                        }
                      }}
                    >
                      <div className="dc-contract-head">
                        <span className="dc-contract-icon"><i className="pi pi-car" /></span>
                        <div>
                          <b>{credito.vehiculo || producto}</b>
                          <span className="tnum">{producto} · {credito.clave_contrato}</span>
                        </div>
                        <em style={{ color: etapa.color, background: etapa.bg }}>{etapaLabelDe(credito.etapa_cobranza, credito.estatus)}</em>
                      </div>

                      <div className="dc-contract-stats">
                        <div className={Number(credito.saldo_vencido || 0) > 0 ? 'danger' : ''}>
                          <span>Vencido</span>
                          <strong className="tnum">{money(Number(credito.saldo_vencido || 0) + Number(credito.mora || 0))}</strong>
                        </div>
                        <div>
                          <span>Próximo pago</span>
                          <strong className="tnum">{credito.proximo_pago?.fecha ? fechaPlantilla(credito.proximo_pago.fecha) : 'Sin próximo pago'}</strong>
                        </div>
                      </div>

                      <div className="dc-contract-meta tnum">Renta mensual {money(credito.renta_mensual)}</div>

                      {activo && credito.sin_telefono && (
                        <div className="dc-contract-phone">
                          <span>Sin teléfono — no se le puede enviar nada.</span>
                          <button type="button" className="dc-send-phone" onClick={() => setTelefonoModal(credito)}><span className="pi pi-phone" />Capturar teléfono</button>
                        </div>
                      )}

                      {activo && (
                        <>
                          {/* Al corriente = nada vencido ni mora: no hay pago que registrar. */}
                          <div className="dc-contract-actions">
                            {!alCorriente && (
                              <Pill icon={pagoModalLoading ? 'pi-spin pi-spinner' : 'pi-wallet'} primary disabled={pagoModalLoading} onClick={() => registrarPagoDeAlerta({ credito_id: credito.id })}>Registrar pago</Pill>
                            )}
                            <Pill icon="pi-file" disabled={!onOpenEdo} onClick={() => onOpenEdo?.(credito.id)} style={alCorriente ? { flex: 1 } : undefined}>Estado</Pill>
                          </div>
                          <Pill
                            icon={pdfBusy || sending ? 'pi-spin pi-spinner' : 'pi-whatsapp'}
                            waBg="#25D366"
                            disabled={!textoHabilitado || pdfBusy || sending}
                            title={textoHabilitado ? 'Genera el PDF y lo envía por este chat' : 'Solo con la ventana de 24 h abierta'}
                            onClick={enviarEstadoCuentaPdf}
                            style={{ width: '100%', marginTop: 8 }}
                          >
                            Enviar estado de cuenta
                          </Pill>
                          {!textoHabilitado && <div className="dc-contract-help">El envío de archivos requiere que el cliente haya escrito en las últimas 24 h.</div>}
                        </>
                      )}
                    </article>
                  );
                })}
              </div>
            </section>
          </div>
        )}
      </div>

      {telefonoModal && (
        <CapturarTelefonoModal
          credito={telefonoModal}
          cliente={selected?.cliente || telefonoModal.cliente || 'este contrato'}
          onClose={cerrarTelefonoModal}
          onGuardado={telefonoGuardado}
        />
      )}
      {templateModal && (
        <ModalPortal>
          <div className="av-modal-backdrop" role="presentation" onMouseDown={() => setTemplateModal(null)}>
            <div className="dc-template-modal" role="dialog" aria-modal="true" aria-label="Vista previa de plantilla" onMouseDown={(event) => event.stopPropagation()}>
              <div className="av-modal-head"><div><h3>Enviar plantilla</h3><p>{templateModal.credito.vehiculo || templateModal.credito.clave_contrato}</p></div><button type="button" onClick={() => setTemplateModal(null)}><i className="pi pi-times" /></button></div>
              <div className="dc-template-options">{templates.map((template) => <button type="button" key={template.key} className={templateModal.template.key === template.key ? 'active' : ''} disabled={templateModal.loading || sending} onClick={() => prepararTemplate(template, templateModal.credito)}>{template.label}</button>)}</div>
              <div className="dc-template-preview">
                {templateModal.loading ? <span><i className="pi pi-spin pi-spinner" /> Preparando con datos actuales…</span> : templateModal.error ? <span className="error"><i className="pi pi-exclamation-circle" /> {templateModal.error}</span> : <p>{vistaPreviaTemplate(templateModal.template.key, templateModal.valores)}</p>}
              </div>
              <div className="av-inline-alert warn"><i className="pi pi-user" />Este envío manual pone la conversación en modo Humano y pausa sus avisos automáticos durante 48 h.</div>
              {templateModal.credito.sin_telefono && <div className="av-inline-alert error"><i className="pi pi-phone" />Falta capturar el teléfono antes de enviar.</div>}
              <div className="av-confirm-actions"><button type="button" onClick={() => setTemplateModal(null)}>Cancelar</button><button type="button" className="primary" disabled={templateModal.loading || Boolean(templateModal.error) || sending || templateModal.credito.sin_telefono} onClick={() => ejecutarTemplate(templateModal.template, null, templateModal.credito, templateModal.params)}><i className={`pi ${sending ? 'pi-spin pi-spinner' : 'pi-send'}`} /> Enviar</button></div>
            </div>
          </div>
        </ModalPortal>
      )}
      {pagoModal && (
        <ModalPortal>
          <RegistrarPagoModal
            {...pagoModal}
            onClose={() => setPagoModal(null)}
            onDone={async () => {
              await refrescarLista();
              await cargarAvisos();
            }}
          />
        </ModalPortal>
      )}
      {colaOpen && (
        <AvisosColaModal
          data={avisosData}
          busy={avisosBusy}
          notice={avisosNotice}
          api={carteraApi}
          onClose={() => setColaOpen(false)}
          onAction={ejecutarAviso}
          onMode={cambiarModoAvisos}
          onTab={(tab) => cargarAvisos(undefined, tab)}
        />
      )}
      {detalleMensaje && (
        <ModalPortal>
          <div className="dc-detail-backdrop" role="presentation" onMouseDown={() => setDetalleMensaje(null)}>
            <aside className="dc-detail-drawer" role="dialog" aria-modal="true" aria-label="Detalle de plantilla" onMouseDown={(event) => event.stopPropagation()}>
              <div className="av-modal-head"><div><h3>Detalle del envío</h3><p>{templatePorNombre.get(detalleMensaje.template_name) || detalleMensaje.template_name}</p></div><button type="button" onClick={() => setDetalleMensaje(null)}><i className="pi pi-times" /></button></div>
              <div className="dc-detail-body">
                <div className="dc-detail-contract"><span>Contrato</span><b>{creditos.find((credito) => Number(credito.id) === Number(detalleMensaje.credito_id))?.clave_contrato || '—'}</b></div>
                <div className="dc-detail-contract"><span>Estado actual</span><b>{detalleMensaje.status || '—'}</b></div>
                <div className="dc-timeline">
                  {[['Enviado', detalleMensaje.sent_at || detalleMensaje.created_at], ['Entregado', detalleMensaje.delivered_at], ['Leído', detalleMensaje.read_at]].map(([label, fecha], index) => (
                    <div className={fecha ? 'done' : ''} key={label}><i className={`pi ${fecha ? 'pi-check' : 'pi-circle'}`} /><span><b>{label}</b><small>{fecha ? fechaHoraCorta(fecha) : 'Pendiente'}</small></span>{index < 2 && <em />}</div>
                  ))}
                </div>
                {detalleMensaje.error_message && <div className="av-inline-alert error"><i className="pi pi-times-circle" />{detalleMensaje.error_message}</div>}
              </div>
            </aside>
          </div>
        </ModalPortal>
      )}
    </div>
  );
};

export default CarteraConversaciones;
